// src/fleet-stats/components/DailyActivityChart.jsx
import React from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    AreaChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Brush,
    ReferenceArea,
    ReferenceLine,
} from "recharts";
import { MousePointer2, BarChart3 } from "lucide-react";
import { C, CARD_BG, CARD_BORDER, SUBTLE_BORDER } from "../constants.js";
import { SectionHeader, brandButton, hoverNeon, unhoverNeon } from "./SharedUI.jsx";

function MarkerDot({ cx, cy, payload, markers, metric }) {
    if (!markers || markers.length === 0) return null;
    const marker = markers.find((m) => m.date === payload.date);
    if (!marker) return null;
    const color = marker.type === "drop" ? "#ff5f5f" : C.neonGreen;
    return (
        <circle
            cx={cx}
            cy={cy}
            r={6}
            fill={color}
            stroke={C.darkBlue}
            strokeWidth={2}
            style={{ cursor: "pointer" }}
        />
    );
}

export function DailyActivityChart({
    fullChartData,
    mainChartData,
    metric,
    setMetric,
    brushIndexes,
    handleBrushChange,
    dragStart,
    dragEnd,
    handleChartMouseDown,
    handleChartMouseMove,
    handleChartMouseUp,
    chartMarkers = [],
}) {
    return (
        <div className="mb-8">
            <SectionHeader
                icon={BarChart3}
                right={
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline-flex items-center gap-1 text-xs" style={{ color: C.grey2 }}>
                            <MousePointer2 size={12} />
                            Drag to select range
                        </span>
                        <div className="flex gap-1">
                            <button
                                onClick={() => setMetric("km")}
                                className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
                                style={brandButton({ active: metric === "km" })}
                                onMouseEnter={hoverNeon}
                                onMouseLeave={unhoverNeon}
                            >
                                Distance (km)
                            </button>
                            <button
                                onClick={() => setMetric("hours")}
                                className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
                                style={brandButton({ active: metric === "hours" })}
                                onMouseEnter={hoverNeon}
                                onMouseLeave={unhoverNeon}
                            >
                                Hours
                            </button>
                        </div>
                    </div>
                }
            >
                Daily activity
            </SectionHeader>

            {/* Legend */}
            {chartMarkers.length > 0 && (
                <div className="flex items-center gap-4 mb-3 text-xs" style={{ color: C.grey2 }}>
                    <span className="flex items-center gap-1.5">
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#ff5f5f" }} />
                        Drop anomaly
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: C.neonGreen }} />
                        Spike anomaly
                    </span>
                </div>
            )}

            <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
                {fullChartData.length === 0 ? (
                    <div className="text-sm" style={{ color: C.grey2 }}>No activity in selected range.</div>
                ) : (
                    <div style={{ width: "100%", height: 430, userSelect: "none" }}>
                        <div style={{ width: "100%", height: 330 }}>
                            <ResponsiveContainer>
                                <ComposedChart
                                    data={mainChartData}
                                    onMouseDown={handleChartMouseDown}
                                    onMouseMove={handleChartMouseMove}
                                    onMouseUp={handleChartMouseUp}
                                    onMouseLeave={() => { if (dragStart) handleChartMouseUp(); }}
                                >
                                    <CartesianGrid stroke={C.grey3} strokeOpacity={0.2} vertical={false} />
                                    <XAxis dataKey="date" tick={{ fill: C.grey1, fontSize: 11 }} minTickGap={40} tickLine={false} axisLine={false} />
                                    <YAxis tick={{ fill: C.grey1, fontSize: 11 }} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        content={({ active, payload, label }) => {
                                            if (!active || !payload || payload.length === 0) return null;
                                            const point = payload[0].payload;
                                            const value = point[metric];
                                            const avg = metric === "km" ? point.km_7d : point.hours_7d;
                                            const marker = chartMarkers.find((m) => m.date === point.date);

                                            return (
                                                <div style={{ background: C.deepBlue, border: `1px solid ${C.grey3}`, borderRadius: 12, padding: "10px 12px", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
                                                    <div style={{ fontSize: 12, color: C.grey2, marginBottom: 6 }}>{label}</div>
                                                    <div style={{ fontSize: 18, fontWeight: 700, color: C.neonGreen, lineHeight: 1.2 }}>
                                                        {value.toFixed(2)} {metric === "km" ? "km" : "h"}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: C.grey1, marginTop: 4 }}>
                                                        7d avg: {avg.toFixed(2)} {metric === "km" ? "km" : "h"}
                                                    </div>
                                                    {point.isWeekend && (
                                                        <div style={{ fontSize: 11, color: C.grey2, marginTop: 4 }}>Weekend</div>
                                                    )}
                                                    {marker && (
                                                        <div style={{ fontSize: 11, marginTop: 4, color: marker.type === "drop" ? "#ff5f5f" : C.neonGreen, fontWeight: 600 }}>
                                                            {marker.type === "drop" ? "⬇ Drop anomaly" : "⬆ Spike anomaly"}: {Math.round(Math.abs(value / avg - 1) * 100)}% vs 7d avg
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />

                                    {/* Weekend shading */}
                                    {mainChartData
                                        .filter((d) => d.isWeekend)
                                        .map((d) => (
                                            <ReferenceArea
                                                key={d.date}
                                                x1={d.date}
                                                x2={d.date}
                                                fill="rgba(255,255,255,0.025)"
                                                strokeOpacity={0}
                                            />
                                        ))}

                                    <Area
                                        type="monotone"
                                        dataKey={metric}
                                        stroke={C.strongBlue}
                                        strokeWidth={1.5}
                                        fill={C.strongBlue}
                                        fillOpacity={0.35}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey={metric === "km" ? "km_7d" : "hours_7d"}
                                        stroke={C.neonGreen}
                                        strokeWidth={2.5}
                                        dot={false}
                                        isAnimationActive={false}
                                    />

                                    {/* Anomaly marker dots via custom dot */}
                                    {chartMarkers.length > 0 && (
                                        <Line
                                            type="monotone"
                                            dataKey={metric}
                                            stroke="transparent"
                                            strokeWidth={0}
                                            dot={(props) => (
                                                <MarkerDot
                                                    key={props.payload?.date}
                                                    {...props}
                                                    markers={chartMarkers}
                                                    metric={metric}
                                                />
                                            )}
                                            isAnimationActive={false}
                                        />
                                    )}

                                    {dragStart && dragEnd && dragStart !== dragEnd && (
                                        <ReferenceArea
                                            x1={dragStart < dragEnd ? dragStart : dragEnd}
                                            x2={dragStart < dragEnd ? dragEnd : dragStart}
                                            strokeOpacity={0.4}
                                            stroke={C.neonGreen}
                                            fill={C.neonGreen}
                                            fillOpacity={0.15}
                                        />
                                    )}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        <div style={{ width: "100%", height: 70, marginTop: 8 }}>
                            <ResponsiveContainer>
                                <AreaChart data={fullChartData}>
                                    <CartesianGrid stroke={C.grey3} strokeOpacity={0.08} vertical={false} />
                                    <XAxis dataKey="date" tick={false} tickLine={false} axisLine={false} />
                                    <YAxis hide />
                                    <Area type="monotone" dataKey={metric} stroke={C.strongBlue} fill={C.strongBlue} fillOpacity={0.25} isAnimationActive={false} />
                                    <Brush
                                        dataKey="date"
                                        height={28}
                                        travellerWidth={10}
                                        stroke={C.grey3}
                                        fill={C.darkBlue}
                                        tickFormatter={() => ""}
                                        startIndex={brushIndexes.startIndex}
                                        endIndex={brushIndexes.endIndex}
                                        onChange={handleBrushChange}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}